// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import axios, { CanceledError } from 'axios';
import { getConfigValue } from '../config';
import { setMaintenance, isMaintenance } from './maintenanceMode';

// Create an axios instance with default config
const axiosInstance = axios.create({
  baseURL: getConfigValue('api.baseUrl', ''), // Use config value with fallback to empty string
  timeout: getConfigValue('api.timeout', 10000),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Changed to false to avoid CORS issues
});

// Track if we've already dispatched a session timeout event
let sessionTimeoutEventFired = false;
let loginTime = 0; // Track when last login occurred
let intentionalLogoutTime = 0; // Track when user intentionally logged out
// Sticky until next login — inactivity timeout and Logout both clear the JWT while
// app-wide providers stay mounted on /login. Without this, pollers keep hitting the
// API and flood the console with 401s.
let suppressAuthErrors = typeof window !== 'undefined' && !localStorage.getItem('token');

/** Paths that may run without a Bearer token (login page / public theme). */
function isAnonymousAllowed(config) {
  if (config?._allowAnonymous || config?._maintenancePoll) return true;
  const url = String(config?.url || '');
  // axios baseURL is typically `/api`, so paths look like `/login`, `/theme/current`, …
  if (url.includes('/login')) return true;
  if (url.includes('/session/bootstrap')) return true;
  if (url.includes('/theme/current') || url.includes('/theme/verify-pin')) return true;
  return false;
}

// Request interceptor for adding auth token — and blocking calls after logout.
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');

    if (!token && !isAnonymousAllowed(config)) {
      // Quiet cancel — App.js already ignores ERR_CANCELED / CanceledError.
      return Promise.reject(new CanceledError('Not authenticated'));
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // If the data is FormData, let the browser set the Content-Type
    // (it needs to include the boundary parameter for multipart/form-data)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Retry configuration for transient errors
// Reduced from 5 retries to 2 for snappier UI - the user shouldn't wait 30+ seconds
// for a failed request to resolve. If the backend is down, fail fast and let the user retry.
const RETRY_CONFIG = {
  maxRetries: 2, // Maximum number of retries (was 5 - too aggressive)
  retryDelay: 500, // Initial delay in ms (was 1000)
  maxDelay: 3000, // Maximum delay in ms (was 10000)
  retryableStatusCodes: [502, 503, 504], // Status codes to retry
  minRetriesBeforeError: 1, // Only show error banner after this many retries (was 3)
};

// Helper function to calculate exponential backoff delay
function getRetryDelay(attempt) {
  const delay = Math.min(RETRY_CONFIG.retryDelay * Math.pow(2, attempt), RETRY_CONFIG.maxDelay);
  // Add jitter to prevent thundering herd
  return delay * (0.5 + Math.random() * 0.5);
}

// Helper function to check if error should be retried
function shouldRetry(error) {
  const method = (error.config?.method || '').toUpperCase();
  if (method === 'POST') {
    // POST is not idempotent — retrying a timed-out create can produce
    // a spurious "already exists" error when the first attempt actually succeeded.
    return false;
  }
  // Device-proxy calls reach a remote Stargate THROUGH the backend. A slow or
  // unreachable device won't recover on an immediate retry — retrying only
  // doubles the wait and, because the browser caps concurrent connections per
  // host (~6), a burst of retrying proxy calls starves every other request and
  // makes the whole app feel slow. Fail fast; callers treat these as best-effort.
  if ((error.config?.url || '').includes('/proxy/')) {
    return false;
  }
  if (!error.response) {
    return true;
  }
  const status = error.response.status;
  return RETRY_CONFIG.retryableStatusCodes.includes(status);
}

// ── Single-flight token refresh (401 recovery) ──────────────────────────────
// On an eligible 401 the response interceptor performs ONE refresh (shared
// across concurrent 401s) and replays the failed request with the new token.
// Uses RAW axios, not axiosInstance — the instance's own interceptors must not
// recurse — so the full `${baseURL}/refresh` path and the Authorization header
// are attached manually. Guards mirror AuthContext.refreshToken(): a refresh
// that resolves after logout must be DISCARDED, never written back (post-logout
// token writes re-arm every poller on /login — the session-resurrection bug).
let refreshInFlight = null;
let lastRefreshOkAt = 0; // reset on login/logout below
const REFRESH_SUCCESS_COOLDOWN_MS = 60 * 1000;

function sharedTokenRefresh() {
  if (refreshInFlight) return refreshInFlight;
  // Success cooldown: single-flight only collapses 401s within one poll
  // cycle. A persistently-401ing proxied device (e.g. a Stargate rejecting
  // its api_key) would otherwise mint a fresh token EVERY cycle — each mint
  // rotates localStorage, re-renders AuthContext consumers, and fires extra
  // /users/me probes. A mint that succeeded moments ago already proves the
  // session is alive; just let the caller replay. Worst case a session that
  // dies inside the cooldown gets one replay-reject cycle before the next
  // real probe ends it.
  if (Date.now() - lastRefreshOkAt < REFRESH_SUCCESS_COOLDOWN_MS) {
    return Promise.resolve(true);
  }
  refreshInFlight = (async () => {
    try {
      const tok = localStorage.getItem('token');
      if (!tok || suppressAuthErrors) return false;
      const base = getConfigValue('api.baseUrl', '');
      const res = await axios.post(`${base}/refresh`, null, {
        headers: { Authorization: `Bearer ${tok}` },
        timeout: 10000,
      });
      // Discard if logout OR any token rotation happened while we awaited:
      // comparing IDENTITY (not just presence) also covers logout→login
      // completing within the 10s request timeout — writing our result then
      // would swap in a token minted for the PREVIOUS user's session.
      if (suppressAuthErrors || localStorage.getItem('token') !== tok) return false;
      const { access_token: accessToken, user } = res.data || {};
      if (!accessToken) return false;
      localStorage.setItem('token', accessToken);
      lastRefreshOkAt = Date.now();
      // AuthContext listens and syncs its React state (with its own guards).
      window.dispatchEvent(
        new CustomEvent('token-refreshed', { detail: { token: accessToken, user } })
      );
      return true;
    } catch (_) {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

// Listen for login / logout so auth-error handling stays quiet on the login page
if (typeof window !== 'undefined') {
  window.addEventListener('login', () => {
    sessionTimeoutEventFired = false;
    loginTime = Date.now();
    intentionalLogoutTime = 0;
    suppressAuthErrors = false;
    lastRefreshOkAt = 0; // new session: first 401 must probe for real
    console.log('Login event received, resetting session timeout tracking');
  });

  // Logout (button or inactivity) clears the JWT while providers stay mounted.
  // Suppress 401 session handling until the next login — not just for 15s.
  window.addEventListener('user-logout', () => {
    intentionalLogoutTime = Date.now();
    suppressAuthErrors = true;
    lastRefreshOkAt = 0;
    console.log('Logout detected, suppressing auth requests/errors until next login');
  });

  // Inactivity path fires session-timeout before user-logout; latch early so
  // in-flight responses that land in between don't re-trigger logout noise.
  window.addEventListener('session-timeout', () => {
    suppressAuthErrors = true;
  });
}

// Response interceptor for handling errors with retry logic
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Canceled because we're on the login page / no token — not an error.
    if (
      axios.isCancel?.(error) ||
      error?.code === 'ERR_CANCELED' ||
      error?.name === 'CanceledError' ||
      error?.message === 'Not authenticated'
    ) {
      return Promise.reject(error);
    }

    // During a restore the backend is intentionally down (the sidecar is swapping
    // the databases). Don't retry or log — fail fast and quietly until services
    // return. Background pollers also self-pause via isMaintenance(), so this is a
    // safety net for any request already in flight when the restore started.
    if (isMaintenance() && !error.config?._maintenancePoll) {
      return Promise.reject(error);
    }

    // After logout, ignore late 401/403 from in-flight calls — no retries, no logs.
    if (suppressAuthErrors && (error.response?.status === 401 || error.response?.status === 403)) {
      return Promise.reject(error);
    }

    // Get retry count from config (default to 0 if not set)
    const retryCount = error.config?._retryCount || 0;

    // Check if this is a retryable error and we haven't exceeded max retries
    // _skipRetry only skips the retry loop — 401 session handling below still runs
    // Guard: error.config must exist to retry (it can be undefined on some network errors)
    if (
      !error.config?._skipRetry &&
      error.config &&
      shouldRetry(error) &&
      retryCount < RETRY_CONFIG.maxRetries
    ) {
      // Increment retry count
      const newRetryCount = retryCount + 1;
      error.config._retryCount = newRetryCount;

      // Calculate delay with exponential backoff
      const delay = getRetryDelay(newRetryCount - 1);

      // Only log the final retry attempt to reduce console noise
      if (newRetryCount >= RETRY_CONFIG.maxRetries) {
        console.warn(
          `[AXIOS] Retryable error (${error.response?.status || 'network'}), retrying in ${Math.round(delay)}ms... (attempt ${newRetryCount}/${RETRY_CONFIG.maxRetries})`
        );
      }

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, delay));

      // Retry the request
      return axiosInstance.request(error.config);
    }

    // Not retryable or max retries reached
    // Attach retry information to error for App.js to decide whether to show banner
    if (error.config) {
      error._retryCount = retryCount;
      error._isPersistentFailure = retryCount >= RETRY_CONFIG.minRetriesBeforeError;
    }

    // Log errors in development
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', error);
    }

    // Only handle 401 errors if they're not from the login endpoint or theme endpoints
    // Theme endpoints should not trigger logout as 401 means wrong PIN, not session expired
    // Also exclude /users/me which is used by AuthContext for token validity checks.
    // NOTE: paths are RELATIVE ('/login', '/refresh') — the instance baseURL
    // carries '/api', so the old '/api/login' / '/api/refresh' patterns never
    // matched anything (dead code masked by suppressAuthErrors).
    const isExcludedEndpoint =
      error.config?.url?.includes('/login') ||
      error.config?.url?.includes('/session/bootstrap') ||
      error.config?.url?.includes('/theme/update') ||
      error.config?.url?.includes('/theme/update-pin') ||
      error.config?.url?.includes('/refresh') ||
      error.config?.url?.includes('/users/me'); // Let AuthContext handle this

    // Don't trigger session timeout for stale requests within 5 seconds of login
    const isStaleRequest = loginTime > 0 && Date.now() - loginTime < 5000;

    // Don't trigger session timeout for 401s after logout (sticky until next login).
    const isAfterLogout = suppressAuthErrors || intentionalLogoutTime > 0;

    // ── 401 → one shared refresh → replay once ─────────────────────────────
    // A 401 usually means the access token aged out (180 min) between the
    // proactive refreshes. Refresh once (single-flight across concurrent
    // 401s) and replay the request with the fresh token instead of ending the
    // session. Replaying is safe for any method, POST included: a 401 means
    // auth was rejected BEFORE the handler (or the proxied device) acted.
    if (
      error.response?.status === 401 &&
      error.config &&
      !error.config._retriedAfterRefresh &&
      !error.config._maintenancePoll &&
      !isExcludedEndpoint &&
      !isAfterLogout &&
      !isStaleRequest
    ) {
      // If the token already rotated since this request was sent (a
      // concurrent 401 refreshed it), skip the mint and just replay.
      const sentAuth = error.config.headers?.Authorization || '';
      const current = localStorage.getItem('token');
      const alreadyRotated = !!current && !!sentAuth && sentAuth !== `Bearer ${current}`;
      const refreshed = alreadyRotated || (await sharedTokenRefresh());
      // Suppression may have latched while we awaited (a concurrent handler
      // dispatched session-timeout) — never keep going after logout.
      if (suppressAuthErrors) {
        return Promise.reject(error);
      }
      // A failed/discarded refresh can still coincide with a LEGITIMATE
      // rotation by another caller (AuthContext.refreshToken, or a competing
      // 401 whose mint made ours discard) — re-check rotation before treating
      // this as a dead session.
      const currentAfter = localStorage.getItem('token');
      const rotatedAfter = !!currentAfter && !!sentAuth && sentAuth !== `Bearer ${currentAfter}`;
      if (refreshed || rotatedAfter) {
        error.config._retriedAfterRefresh = true;
        return axiosInstance.request(error.config);
      }
      // Refresh failed → fall through to the session-timeout logic below.
    }

    if (
      error.response &&
      error.response.status === 401 &&
      !isExcludedEndpoint &&
      !isStaleRequest &&
      !isAfterLogout &&
      // A 401 on a request already replayed with a FRESHLY-minted token
      // proves the session itself is fine — the 401 is endpoint-local (e.g. a
      // Stargate behind /api/proxy rejecting its device api_key). Reject to
      // the caller; ending the whole session here was how a single device
      // 401 could kick the user to the login page.
      !error.config?._retriedAfterRefresh
    ) {
      let hasActiveOperations = false;
      try {
        const firmwareState = localStorage.getItem('firmwareUpdateState');
        if (firmwareState) {
          const state = JSON.parse(firmwareState);
          hasActiveOperations =
            (state.activeUploads && state.activeUploads.length > 0) ||
            state.updateInProgress ||
            (state.updateStatus && Object.keys(state.updateStatus).length > 0) ||
            (state.uploadProgress &&
              Object.values(state.uploadProgress).some((p) => p > 0 && p < 100));
        }
      } catch (e) {
        /* ignore */
      }
      if (!hasActiveOperations) {
        try {
          const pullState = localStorage.getItem('pullDeploymentStatus');
          if (pullState) {
            const parsed = JSON.parse(pullState);
            const active = [
              'downloading',
              'pulling',
              'updating',
              'rebooting',
              'uploading',
              'rauc_complete',
              'pending',
            ];
            hasActiveOperations = Object.values(parsed).some((s) => s && active.includes(s.status));
          }
          if (!hasActiveOperations && localStorage.getItem('pullFirmwareId')) {
            hasActiveOperations = true;
          }
        } catch (e) {
          /* ignore */
        }
      }
      if (!hasActiveOperations) {
        try {
          const containerJobs = localStorage.getItem('containerPullJobs_v2');
          if (containerJobs) {
            const parsed = JSON.parse(containerJobs);
            hasActiveOperations = Object.values(parsed).some((job) =>
              Object.values(job.stargates || {}).some(
                (s) => s.status && !['completed', 'failed'].includes(s.status)
              )
            );
          }
        } catch (e) {
          /* ignore */
        }
      }

      // If there are active operations, don't immediately logout
      // The session might have expired but user is actively using the app
      // Try to refresh token first
      if (hasActiveOperations) {
        console.warn(
          '[AXIOS] 401 error detected but active operations found. Attempting token refresh instead of immediate logout.'
        );
        // Dispatch a token refresh attempt event
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('token-refresh-needed', {
              detail: { url: error.config?.url },
            })
          );
        }
        // Don't dispatch session-timeout yet - let AuthContext handle token refresh
        return Promise.reject(error);
      }

      // Only dispatch the event once to prevent multiple logout calls
      if (!sessionTimeoutEventFired) {
        sessionTimeoutEventFired = true;

        // Notify the app (e.g., App.js) to show a session timeout message
        if (typeof window !== 'undefined') {
          console.log('Dispatching session-timeout event from axios interceptor');
          sessionStorage.setItem('sessionTimedOut', '1');
          window.dispatchEvent(new CustomEvent('session-timeout'));
        }

        // Reset the flag after a delay to allow for new sessions
        setTimeout(() => {
          sessionTimeoutEventFired = false;
        }, 5000); // Increased delay
      }
      // The 'session-timeout' event will be handled by App.js or AuthContext
      // to perform the actual logout and redirect logic.
      // No direct localStorage manipulation or redirect here.
    } else if (isStaleRequest) {
      console.log('Ignoring 401 from stale request after recent login');
    }
    // After logout: stay quiet (no console spam from late 401s).

    return Promise.reject(error);
  }
);

// ── Restore (maintenance) detection ─────────────────────────────────────────
// Poll the backup sidecar (nginx-routed at /api/backup/*, which stays up during a
// restore) and flip the shared maintenance flag while a restore job is running, so
// background pollers across the app pause instead of flooding 502s. /api/backup/
// progress is admin-only; for non-admins (or when logged out) we simply treat it as
// "not in maintenance" and let normal behavior proceed.
function tokenRole() {
  try {
    const t = localStorage.getItem('token');
    if (!t) return null;
    const payload = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.role || null;
  } catch (_) {
    return null;
  }
}

async function pollRestoreState() {
  if (typeof window === 'undefined') return;
  // After logout / session timeout (sticky until the next login), do not poll
  // at all. tokenRole() only parses the JWT client-side — a stale token left in
  // localStorage would otherwise produce a REAL network 401 every 5s, which the
  // browser console always logs regardless of JS error suppression.
  if (suppressAuthErrors) {
    setMaintenance(false);
    return;
  }
  // /api/backup/progress is admin-only — only admins can run a restore. Skip for
  // everyone else so we don't generate 403 noise in their console every few seconds.
  if (tokenRole() !== 'admin') {
    setMaintenance(false);
    return;
  }
  try {
    // NOTE: axiosInstance baseURL already includes /api, so the path is /backup/progress
    // (resolves to /api/backup/progress). Using /api/backup/progress here doubles it.
    const res = await axiosInstance.get('/backup/progress', {
      _skipRetry: true,
      _maintenancePoll: true,
      timeout: 4000,
    });
    const job = res.data || {};
    setMaintenance(job.type === 'restore' && job.status === 'running');
  } catch (_) {
    // 401/403 (non-admin), or a transient sidecar error — don't assume maintenance.
    setMaintenance(false);
  }
}

if (typeof window !== 'undefined') {
  // Lightweight (every 5s). Cheap JSON from the always-up sidecar.
  setInterval(pollRestoreState, 5000);
  pollRestoreState();
}

export default axiosInstance;
